<template>
    <v-dialog v-model="showDialog"
              class="date-time-dialog"
              transition="dialog-bottom-transition"
              width="400px">
        <v-card class="date-time-form text-primary ma-0">
            <v-card-title>
                Выбор даты и времени
            </v-card-title>

            <v-card-text class="pa-4 pt-0">
                <div class="d-flex justify-space-between py-2">
                    <span class="d-flex align-end">Время начала: </span>
                    <TimeSelector v-model="startTime"
                                  label="Начало" />
                </div>
                <div class="d-flex justify-space-between py-2">
                    <span class="d-flex align-end">Время окончания: </span>
                    <TimeSelector v-model="endTime"
                                  label="Окончание" />
                </div>
                <div class="d-flex justify-space-between py-2">
                    <span class="d-flex align-end">Длительность: </span>
                    <TimeSelector v-model="duration"
                                  label="Длительность" />
                </div>
                <div class="d-flex justify-space-between py-2">
                    <span class="d-flex align-end">Дата: </span>
                    <DateSelector v-model="date"
                                  label="Дата" />
                </div>
            </v-card-text>

            <v-card-actions class="pr-4 pb-4 pt-0">
                <v-spacer />
                <v-btn color="secondary"
                       variant="elevated"
                       @click="$emit('hide')">Закрыть</v-btn>
                <v-btn color="primary"
                       variant="elevated"
                       :disabled="!date || !date.isValid"
                       @click="save">Применить</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
    import { DateTime } from "luxon";
    import LuxonAdapter from "@date-io/luxon";

    import TimeSelector from "@/components/TimeSelector.vue";
    import DateSelector from "@/components/DateSelector.vue";

    export default {
        components: {
            TimeSelector,
            DateSelector
        },

        props: {
            modelValue: Object
        },

        emits: ["update:modelValue", "hide"],

        data() {
            return {
                adapter: new LuxonAdapter(),

                date: this.modelValue?.date,
                startTime: this.modelValue?.startTime,
                endTime: this.modelValue?.endTime,
                duration: this.modelValue?.duration,
                taskNumber: this.modelValue?.taskNumber,

                showDialog: true,
                enableWatch: true
            };
        },

        watch: {
            modelValue(newValue) {
                this.setData(newValue);
            },

            showDialog(newValue) {
                if (newValue === false) {
                    this.$emit("hide");
                }
            },

            startTime(newValue) {
                if (!this.enableWatch) {
                    return;
                }

                const newStartTime = this.getValidTime(newValue);
                this.startTime = newStartTime;

                if (!this.isValidTime(newStartTime)) {
                    return;
                }

                this.enableWatch = false;

                if (this.endTime) {
                    if (this.isSmallerTime(newStartTime, this.endTime)) {
                        this.duration = this.getTimesDifference(this.endTime, newStartTime);
                    } else {
                        this.endTime = "";
                    }
                } else if (this.duration) {
                    const newEndTime = this.getTimesSum(newStartTime, this.duration);

                    if (this.isValidTime(newEndTime) && this.isSmallerTime(newStartTime, newEndTime)) {
                        this.endTime = newEndTime;
                    } else {
                        this.endTime = "";
                    }
                }

                this.enableWatch = true;
            },

            endTime(newValue) {
                if (!this.enableWatch) {
                    return;
                }

                const newEndTime = this.getValidTime(newValue);
                this.endTime = newEndTime;

                if (!this.isValidTime(newEndTime)) {
                    return;
                }

                this.enableWatch = false;

                if (this.startTime) {
                    if (this.isSmallerTime(this.startTime, newEndTime)) {
                        this.duration = this.getTimesDifference(newEndTime, this.startTime);
                    } else {
                        this.startTime = "";
                    }
                } else if (this.duration) {
                    const newStartTime = this.getTimesDifference(newEndTime, this.duration);

                    if (this.isValidTime(newStartTime) && this.isSmallerTime(newStartTime, newEndTime)) {
                        this.startTime = newStartTime;
                    } else {
                        this.startTime = "";
                    }
                }

                this.enableWatch = true;
            },

            duration(newValue) {
                if (!this.enableWatch) {
                    return;
                }

                const newDuration = this.getValidTime(newValue);
                this.duration = newDuration;

                if (!this.isValidTime(newDuration)) {
                    return;
                }

                this.enableWatch = false;

                if (this.startTime) {
                    const newEndTime = this.getTimesSum(this.startTime, newDuration);

                    if (this.isValidTime(newEndTime) && this.isSmallerTime(this.startTime, newEndTime)) {
                        this.endTime = newEndTime;
                    } else {
                        this.endTime = "";
                    }
                } else if (this.endTime) {
                    const newStartTime = this.getTimesDifference(this.endTime, newDuration);

                    if (this.isValidTime(newStartTime) && this.isSmallerTime(newStartTime, this.endTime)) {
                        this.startTime = newStartTime;
                    } else {
                        this.startTime = "";
                    }
                }

                this.enableWatch = true;
            }
        },

        methods: {
            setData(value) {
                this.date = value?.date;
                this.startTime = value?.startTime;
                this.endTime = value?.endTime;
                this.duration = value?.duration;
                this.taskNumber = value?.taskNumber;
            },

            getValidTime(value) {
                if (!value) {
                    return "";
                }
                if (typeof value !== "string") {
                    return "";
                }
                if (value.length !== 5) {
                    return "";
                }
                if (value[2] !== ":") {
                    return "";
                }

                const hh = value.substring(0, 2);
                const mm = value.substring(3, 5);

                if (hh === "HH" && mm === "mm") {
                    return "";
                }
                if (hh === "HH") {
                    return "00:" + mm;
                }
                if (mm === "mm") {
                    return hh + ":00";
                }

                return value;
            },

            isValidTime(time) {
                const dateTime = DateTime.fromISO(time);

                return dateTime.isValid;
            },

            isSmallerTime(time1, time2) {
                const dateTime1 = DateTime.fromISO(time1);
                const dateTime2 = DateTime.fromISO(time2);

                return dateTime1 < dateTime2;
            },

            getTimesSum(time1, time2) {
                const dateTime1 = DateTime.fromISO(time1);
                const dateTime2 = DateTime.fromISO(time2);

                return dateTime1
                    .plus({ hours: dateTime2.hour, minutes: dateTime2.minute })
                    .toFormat("HH:mm");
            },

            getTimesDifference(time1, time2) {
                const dateTime1 = DateTime.fromISO(time1);
                const dateTime2 = DateTime.fromISO(time2);

                return dateTime1
                    .diff(dateTime2)
                    .shiftTo("hours", "minutes")
                    .toFormat("hh:mm");
            },

            save() {
                this.$emit("hide");

                const isStartTime = this.startTime && this.isValidTime(this.startTime);
                const isEndTime = this.endTime && this.isValidTime(this.endTime);
                const isDuration = this.duration && this.isValidTime(this.duration);
                const isAll = isStartTime && isEndTime && isDuration;

                this.$emit("update:modelValue", {
                    date: this.date,
                    startTime: isStartTime ? this.startTime : null,
                    endTime: isAll ? this.endTime : null,
                    duration: isDuration ? this.duration : null,
                    taskNumber: this.modelValue?.date !== null && this.date.hasSame(this.modelValue?.date, "day")
                        ? this.taskNumber
                        : null
                });
            }
        }
    };
</script>

<style lang="scss">
    .date-time-form {
    }
</style>